import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

import { ModernButton } from '../../components/ui/ModernButton';
import { StatusIndicator, CountIndicator, AgentRoleIndicator } from '../../components/ui/ModernIndicators';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { PickList } from 'primereact/picklist';
import { Avatar } from 'primereact/avatar';
import { TabView, TabPanel } from 'primereact/tabview';
import { Message } from 'primereact/message';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useAuth } from '../../contexts/AuthContext';
import { CustomerService } from '../../services/customerService';
import { AgentService } from '../../services/agentService';
import { Customer, Agent } from '../../types/agent';
import { getAgentInitials, getAgentDisplayName } from '../../utils/stringHelpers';
import './CustomerAssignmentsPage.css';

interface AssignmentStats {
	totalCustomers: number;
	assignedCustomers: number;
	unassignedCustomers: number;
	averageCustomersPerAgent: number;
	agentsWithCustomers: number;
	agentsWithoutCustomers: number;
}

export const CustomerAssignmentsPage: React.FC = () => {
	const { currentAgent } = useAuth();
	const toast = useRef<Toast>(null);

	const [activeTab, setActiveTab] = useState(0);
	const [customers, setCustomers] = useState<Customer[]>([]);
	const [agents, setAgents] = useState<Agent[]>([]);
	const [loading, setLoading] = useState(true);
	const [stats, setStats] = useState<AssignmentStats | null>(null);
	const [showAssignDialog, setShowAssignDialog] = useState(false);
	const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
	// Bumped whenever an assignment confirmation closes, to reset the inline dropdowns
	const [dropdownResetKey, setDropdownResetKey] = useState(0);

	const [agentSearchTerm, setAgentSearchTerm] = useState('');
	const [customerSearchTerm, setCustomerSearchTerm] = useState('');
	const [unassignedSearchTerm, setUnassignedSearchTerm] = useState('');

	// For PickList component
	const [sourceCustomers, setSourceCustomers] = useState<Customer[]>([]);
	const [targetCustomers, setTargetCustomers] = useState<Customer[]>([]);
	const [agentCustomerCounts, setAgentCustomerCounts] = useState<Record<string, number>>({});
	const [hasMoreCustomers, setHasMoreCustomers] = useState(false);

	const loadData = useCallback(async () => {
		setLoading(true);
		try {
			// Headline numbers come from the aggregate call so they stay exact
			// regardless of how many customers are loaded for the tables below.
			const [customersResult, agentsData, counts] = await Promise.all([
				CustomerService.getCustomers({ limit: 200 }),
				AgentService.getAllAgents(),
				CustomerService.getCustomerCounts()
			]);

			setCustomers(customersResult.customers);
			setHasMoreCustomers(customersResult.hasMore);
			setAgents(agentsData);

			applyCounts(counts, agentsData);
		} catch (error) {

			toast.current?.show({
				severity: 'error',
				summary: 'Error',
				detail: 'Failed to load data',
				life: 3000
			});
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		if (currentAgent?.role === 'master_agent' || currentAgent?.role === 'super_agent') {
			loadData();
		}
	}, [currentAgent, loadData]);

	/**
	 * Stats come from server-side counts rather than from whatever slice of
	 * customers happens to be loaded, so they no longer drift when the table
	 * shows only the first page.
	 */
	const applyCounts = (
		counts: { total: number; unassigned: number; byAgent: Record<string, number> },
		agentList: Agent[]
	) => {
		const agentsWithCustomers = Object.values(counts.byAgent).filter(n => n > 0).length;
		const totalAssignedCustomers = counts.total - counts.unassigned;

		setAgentCustomerCounts(counts.byAgent);
		setStats({
			totalCustomers: counts.total,
			assignedCustomers: totalAssignedCustomers,
			unassignedCustomers: counts.unassigned,
			averageCustomersPerAgent: agentsWithCustomers > 0
				? Math.round(totalAssignedCustomers / agentsWithCustomers)
				: 0,
			agentsWithCustomers,
			agentsWithoutCustomers: agentList.length - agentsWithCustomers
		});
	};

	const handleAssignCustomers = async () => {
		if (!selectedAgent || targetCustomers.length === 0) return;

		try {
			const customerIds = targetCustomers.map(c => c.id);
			await CustomerService.bulkAssignCustomers(customerIds, selectedAgent.id);

			toast.current?.show({
				severity: 'success',
				summary: 'Success',
				detail: `Assigned ${targetCustomers.length} customers to ${getAgentDisplayName(selectedAgent)}`,
				life: 3000
			});

			setShowAssignDialog(false);
			setSelectedAgent(null);
			setSourceCustomers([]);
			setTargetCustomers([]);
			await loadData();
		} catch (error) {

			toast.current?.show({
				severity: 'error',
				summary: 'Error',
				detail: 'Failed to assign customers',
				life: 3000
			});
		}
	};

	const performReassignCustomer = async (customer: Customer, newAgentId: string | null) => {
		try {
			if (newAgentId === null || newAgentId === undefined) {
				// Unassign customer
				await CustomerService.updateCustomer(customer.id, { assignedAgentId: undefined });
				await loadData();

				toast.current?.show({
					severity: 'success',
					summary: 'Success',
					detail: 'Customer unassigned successfully',
					life: 3000
				});
			} else {
				// Reassign to new agent
				await CustomerService.assignCustomerToAgent(customer.id, newAgentId);
				await loadData();

				const agent = agents.find(a => a.id === newAgentId);
				toast.current?.show({
					severity: 'success',
					summary: 'Success',
					detail: `Customer reassigned to ${getAgentDisplayName(agent)}`,
					life: 3000
				});
			}
		} catch (error) {
			toast.current?.show({
				severity: 'error',
				summary: 'Error',
				detail: 'Failed to update customer assignment',
				life: 3000
			});
		}
	};

	const handleReassignCustomer = (customer: Customer, newAgentId: string | null) => {
		const customerName = `${customer.firstName} ${customer.lastName}`;
		const currentlyAssignedAgent = customer.assignedAgentId
			? agents.find(a => a.id === customer.assignedAgentId)
			: undefined;
		const currentAgentName = currentlyAssignedAgent ? getAgentDisplayName(currentlyAssignedAgent) : null;

		// Any outcome (accept, reject, escape) re-mounts the dropdowns so a cancelled
		// confirmation cannot leave the UI showing a change that was never committed
		const onHide = () => setDropdownResetKey(prev => prev + 1);

		if (newAgentId === null || newAgentId === undefined) {
			confirmDialog({
				message: currentAgentName
					? `Unassign ${customerName} from ${currentAgentName}? The customer will be left without an assigned agent.`
					: `Unassign ${customerName}? The customer will be left without an assigned agent.`,
				header: 'Confirm Unassign',
				icon: 'pi pi-exclamation-triangle',
				acceptClassName: 'p-button-danger',
				acceptLabel: 'Unassign',
				accept: () => performReassignCustomer(customer, null),
				onHide
			});
			return;
		}

		const newAgent = agents.find(a => a.id === newAgentId);
		const newAgentName = getAgentDisplayName(newAgent);

		confirmDialog({
			message: currentAgentName
				? `Reassign ${customerName} from ${currentAgentName} to ${newAgentName}?`
				: `Assign ${customerName} to ${newAgentName}?`,
			header: currentAgentName ? 'Confirm Reassign' : 'Confirm Assign',
			icon: 'pi pi-exclamation-triangle',
			acceptLabel: currentAgentName ? 'Reassign' : 'Assign',
			accept: () => performReassignCustomer(customer, newAgentId),
			onHide
		});
	};

	const openAssignDialog = (agent: Agent) => {
		setSelectedAgent(agent);
		const unassigned = customers.filter(c => !c.assignedAgentId);
		setSourceCustomers(unassigned);
		setTargetCustomers([]);
		setShowAssignDialog(true);
	};

	// Counted server-side. Deriving this from the loaded page would undercount
	// as soon as a book runs past the first page.
	const getAgentCustomerCount = (agentId: string) => agentCustomerCounts[agentId] || 0;

	// Column templates
	const agentBodyTemplate = (rowData: Agent) => {

		return (
			<div className="agent-info">
				<Avatar
					label={getAgentInitials(rowData)}
					className="agent-avatar"
					style={{ backgroundColor: '#3498db', color: 'white' }}
				/>
				<div className="agent-details">
					<span className="agent-name">{getAgentDisplayName(rowData)}</span>
					<span className="agent-email">{rowData.email}</span>
				</div>
			</div>
		);
	};

	const customerCountBodyTemplate = (rowData: Agent) => {
		const count = getAgentCustomerCount(rowData.id);
		const color = count > 10 ? '#e74c3c' : count > 5 ? '#f39c12' : '#2ecc71';

		return (
			<div className="customer-count-simple">
				<span style={{ color, fontWeight: 600, fontSize: '1.125rem' }}>{count}</span>
			</div>
		);
	};

	const roleBodyTemplate = (rowData: Agent) => {
		return <AgentRoleIndicator role={rowData.role} />;
	};

	const actionsBodyTemplate = (rowData: Agent) => {
		return (
			<div className="action-buttons">
				<ModernButton
					variant="primary"
					size="sm"
					icon="pi pi-users"
					onClick={() => openAssignDialog(rowData)}
					title="Assign customers to this agent"
				>
					Assign
				</ModernButton>
			</div>
		);
	};

	const customerAgentBodyTemplate = (rowData: Customer) => {
		if (!rowData.assignedAgentId) {
			return <StatusIndicator status="inactive" label="Unassigned" />;
		}

		const agent = agents.find(a => a.id === rowData.assignedAgentId);
		if (!agent) return null;

		return (
			<div className="assigned-agent">
				<Avatar
					label={getAgentInitials(agent)}
					size="normal"
					style={{ backgroundColor: '#3498db', color: 'white' }}
				/>
				<span>{getAgentDisplayName(agent)}</span>
			</div>
		);
	};

	const reassignBodyTemplate = (rowData: Customer) => {
		const agentOptions = [
			{ label: '— Unassign —', value: null },
			...agents.map(a => ({ label: getAgentDisplayName(a), value: a.id }))
		];

		return (
			<div className="reassign-actions-compact">
				<Dropdown
					key={`reassign-${rowData.id}-${dropdownResetKey}`}
					value={rowData.assignedAgentId}
					options={agentOptions}
					onChange={(e) => handleReassignCustomer(rowData, e.value)}
					placeholder="Select Agent"
					className="reassign-dropdown-compact"
					disabled={agents.length === 0}
					style={{ width: '100%' }}
				/>
			</div>
		);
	};

	const leadScoreBodyTemplate = (rowData: Customer) => {
		const score = rowData.leadScore || 0;
		const getScoreClass = (score: number) => {
			if (score >= 80) return 'high';
			if (score >= 50) return 'medium';
			return 'low';
		};

		return (
			<div className={`lead-score ${getScoreClass(score)}`}>
				<span>{score}</span>
			</div>
		);
	};

	// PickList templates
	const customerTemplate = (customer: Customer) => {
		return (
			<div className="customer-item">
				<div className="customer-info">
					<span className="customer-name">{customer.firstName} {customer.lastName}</span>
					<span className="customer-email">{customer.email}</span>
				</div>
				<CountIndicator count={customer.leadScore || 0} />
			</div>
		);
	};

	const filteredAgents = agents.filter(agent => {
		if (!agentSearchTerm) return true;
		const search = agentSearchTerm.toLowerCase();
		const agentName = getAgentDisplayName(agent).toLowerCase();
		return (
			agentName.includes(search) ||
			agent.email.toLowerCase().includes(search)
		);
	});

	const filteredCustomers = customers.filter(customer => {
		if (!customerSearchTerm) return true;
		const search = customerSearchTerm.toLowerCase();
		return (
			customer.firstName.toLowerCase().includes(search) ||
			customer.lastName.toLowerCase().includes(search) ||
			customer.email.toLowerCase().includes(search)
		);
	});

	const filteredUnassignedCustomers = customers.filter(customer => {
		if (customer.assignedAgentId) return false;
		if (!unassignedSearchTerm) return true;
		const search = unassignedSearchTerm.toLowerCase();
		return (
			customer.firstName.toLowerCase().includes(search) ||
			customer.lastName.toLowerCase().includes(search) ||
			customer.email.toLowerCase().includes(search)
		);
	});

	if (currentAgent?.role !== 'master_agent' && currentAgent?.role !== 'super_agent') {
		return (
			<div className="customer-assignments-page">
				<Card className="access-denied-card">
					<div className="access-denied-content">
						<i className="pi pi-lock" style={{ fontSize: '3rem', color: '#e74c3c' }}></i>
						<h3>Access Denied</h3>
						<p>Only Master Agents and Super Agents can access customer assignments.</p>
					</div>
				</Card>
			</div>
		);
	}

	return (
		<div className="customer-assignments-page">
			<Toast ref={toast} />
			<ConfirmDialog />

			<div className="page-header">
				<h1>Customer Assignments</h1>
				<p>Manage customer assignments across your agent team</p>
			</div>

			{/* Stats Overview */}
			{stats && (
				<div className="stats-grid">
					<Card className="stat-card">
						<div className="stat-content">
							<div className="stat-icon total-icon">
								<i className="pi pi-users"></i>
							</div>
							<div className="stat-details">
								<h3>{stats.totalCustomers}</h3>
								<p>Total Customers</p>
							</div>
						</div>
					</Card>

					<Card className="stat-card">
						<div className="stat-content">
							<div className="stat-icon assigned-icon">
								<i className="pi pi-check-circle"></i>
							</div>
							<div className="stat-details">
								<h3>{stats.assignedCustomers}</h3>
								<p>Assigned</p>
								<small>{stats.totalCustomers > 0 ? Math.round((stats.assignedCustomers / stats.totalCustomers) * 100) : 0}%</small>
							</div>
						</div>
					</Card>

					<Card className="stat-card">
						<div className="stat-content">
							<div className="stat-icon unassigned-icon">
								<i className="pi pi-exclamation-circle"></i>
							</div>
							<div className="stat-details">
								<h3>{stats.unassignedCustomers}</h3>
								<p>Unassigned</p>
							</div>
						</div>
					</Card>

					<Card className="stat-card">
						<div className="stat-content">
							<div className="stat-icon average-icon">
								<i className="pi pi-chart-bar"></i>
							</div>
							<div className="stat-details">
								<h3>{stats.averageCustomersPerAgent}</h3>
								<p>Avg per Agent</p>
							</div>
						</div>
					</Card>
				</div>
			)}

			<TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
				<TabPanel header="Agent Overview">
					<Card className="agents-card">
						<div className="agents-header">
							<div className="search-box">
								<span className="pi pi-search"></span>
								<InputText
									value={agentSearchTerm}
									onChange={(e) => setAgentSearchTerm(e.target.value)}
									placeholder="Search agents..."
									className="search-input"
								/>
							</div>
						</div>

						<DataTable
							value={filteredAgents}
							loading={loading}
							paginator
							rows={10}
							emptyMessage="No agents found."
							className="agents-table"
							responsiveLayout="scroll"
						>
							<Column field="agent" header="AGENT" body={agentBodyTemplate} style={{ width: '35%' }} />
							<Column field="role" header="ROLE" body={roleBodyTemplate} style={{ width: '25%' }} />
							<Column field="customerCount" header="CUSTOMERS" body={customerCountBodyTemplate} sortable style={{ width: '20%', textAlign: 'center' }} />
							<Column header="ACTIONS" body={actionsBodyTemplate} style={{ width: '20%', textAlign: 'center' }} />
						</DataTable>
					</Card>
				</TabPanel>

				<TabPanel header="All Customers">
					<Card className="customers-card">
						{/*
							This table shows a loaded page, while the Agent Overview counts
							are exact server-side totals. Say so rather than letting the two
							disagree silently.
						*/}
						{hasMoreCustomers && stats && (
							<Message
								severity="info"
								text={`Showing the first ${customers.length} of ${stats.totalCustomers} customers.`}
								className="assignments-partial-notice"
							/>
						)}
						<div className="customers-header">
							<div className="search-box">
								<span className="pi pi-search"></span>
								<InputText
									value={customerSearchTerm}
									onChange={(e) => setCustomerSearchTerm(e.target.value)}
									placeholder="Search customers..."
									className="search-input"
								/>
							</div>
							<div className="header-stats">
								<CountIndicator count={stats?.unassignedCustomers || 0} label="unassigned" />
							</div>
						</div>

						<DataTable
							value={filteredCustomers}
							loading={loading}
							paginator
							rows={10}
							emptyMessage="No customers found."
							className="customers-table"
							responsiveLayout="scroll"
						>
							<Column field="name" header="CUSTOMER" body={(rowData) => (
								<div className="customer-name-cell">
									<span className="customer-name-text">{rowData.firstName} {rowData.lastName}</span>
									<span className="customer-email">{rowData.email}</span>
								</div>
							)} style={{ width: '30%' }} />
							<Column field="leadScore" header="SCORE" body={leadScoreBodyTemplate} sortable style={{ width: '15%', textAlign: 'center' }} />
							<Column field="assignedAgent" header="ASSIGNED TO" body={customerAgentBodyTemplate} style={{ width: '30%' }} />
							<Column header="REASSIGN" body={reassignBodyTemplate} style={{ width: '25%' }} />
						</DataTable>
					</Card>
				</TabPanel>

				<TabPanel header="Unassigned">
					<Card className="unassigned-card">
						<div className="unassigned-header">
							<div className="unassigned-title-section">
								<h3>Unassigned Customers ({stats?.unassignedCustomers || 0})</h3>
								{stats && stats.unassignedCustomers > 0 && (
									<p className="unassigned-subtitle">
										These customers need to be assigned to an agent
									</p>
								)}
							</div>
							<div className="search-box">
								<span className="pi pi-search"></span>
								<InputText
									value={unassignedSearchTerm}
									onChange={(e) => setUnassignedSearchTerm(e.target.value)}
									placeholder="Search unassigned..."
									className="search-input"
								/>
							</div>
						</div>

						<DataTable
							value={filteredUnassignedCustomers}
							loading={loading}
							paginator
							rows={10}
							emptyMessage={
								<div className="empty-state-inline">
									<i className="pi pi-check-circle"></i>
									<span>All customers are assigned!</span>
								</div>
							}
							className="unassigned-table"
							responsiveLayout="scroll"
						>
							<Column field="name" header="CUSTOMER" body={(rowData) => (
								<div className="customer-name-cell">
									<span className="customer-name-text">{rowData.firstName} {rowData.lastName}</span>
									<span className="customer-email">{rowData.email}</span>
								</div>
							)} style={{ width: '35%' }} />
							<Column field="leadScore" header="SCORE" body={leadScoreBodyTemplate} sortable style={{ width: '15%', textAlign: 'center' }} />
							<Column field="createdAt" header="ADDED" body={(rowData) => (
								<span className="date-text">{new Date(rowData.createdAt).toLocaleDateString()}</span>
							)} sortable style={{ width: '20%' }} />
							<Column header="ASSIGN TO AGENT" body={(rowData) => (
								<Dropdown
									key={`assign-${rowData.id}-${dropdownResetKey}`}
									value={null}
									options={agents.map(a => ({
										label: getAgentDisplayName(a),
										value: a.id
									}))}
									onChange={(e) => handleReassignCustomer(rowData, e.value)}
									placeholder="Select Agent"
									className="assign-dropdown-table"
									style={{ width: '100%' }}
								/>
							)} style={{ width: '30%' }} />
						</DataTable>
					</Card>
				</TabPanel>
			</TabView>

			{/* Assign Dialog */}
			<Dialog
				visible={showAssignDialog}
				onHide={() => {
					setShowAssignDialog(false);
					setSelectedAgent(null);
					setSourceCustomers([]);
					setTargetCustomers([]);
				}}
				header={`Assign Customers to ${getAgentDisplayName(selectedAgent)}`}
				style={{ width: '70vw' }}
				breakpoints={{ '960px': '85vw', '640px': '95vw' }}
				footer={
					<div className="modern-form-actions">
						<ModernButton variant="ghost" onClick={() => setShowAssignDialog(false)}>
							Cancel
						</ModernButton>
						<ModernButton
							variant="primary"
							onClick={handleAssignCustomers}
							disabled={targetCustomers.length === 0}
						>
							Assign {targetCustomers.length} Customers
						</ModernButton>
					</div>
				}
			>
				<div className="assign-dialog-content">
					<p>Select customers to assign to this agent. Drag customers from left to right or use the arrow buttons.</p>

					<PickList
						source={sourceCustomers}
						target={targetCustomers}
						dataKey="id"
						onChange={(e) => {
							setSourceCustomers(e.source);
							setTargetCustomers(e.target);
						}}
						itemTemplate={customerTemplate}
						sourceHeader="Available Customers"
						targetHeader="Selected Customers"
						sourceStyle={{ height: '400px' }}
						targetStyle={{ height: '400px' }}
						showSourceControls={false}
						showTargetControls={false}
					/>
				</div>
			</Dialog>
		</div>
	);
};

export default CustomerAssignmentsPage;